import React, { useEffect, useState } from 'react';
import { useDataProvider, useNotify, useRefresh, useVersion } from 'react-admin';

import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CircularProgress from '@material-ui/core/CircularProgress';
import Divider from '@material-ui/core/Divider';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core';
import classNames from 'classnames';

import { Confirmation } from 'components/common';

import { UserDetails } from '../types';

const useStyles = makeStyles((theme) => ({
  card: {
    padding: theme.spacing(3),
  },
  label: {
    fontSize: 16,
    color: theme.palette.secondary.contrastText,
    fontWeight: 700,
  },
  button: {
    color: theme.palette.secondary.contrastText,
    textTransform: 'none',
    minWidth: '110px',
  },
  actionGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
  },
  sectionTitle: {
    fontSize: 16,
    color: theme.palette.secondary.contrastText,
    fontWeight: 700,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '16px',
  },
  statusDescription: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
  statusChip: {
    padding: theme.spacing(0.5, 1.5),
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 700,
  },
  statusChipSuccess: {
    backgroundColor: '#DFFFE6',
    color: '#0CA82D',
  },
  statusChipWarning: {
    backgroundColor: '#FFF4D6',
    color: '#C87900',
  },
  statusChipError: {
    backgroundColor: '#FDE0E0',
    color: '#B00020',
  },
  statusChipNeutral: {
    backgroundColor: '#E8EBF0',
    color: theme.palette.text.primary,
  },
  helperText: {
    color: theme.palette.text.secondary,
    fontSize: 13,
    marginTop: theme.spacing(0.5),
  },
  loadingRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginTop: theme.spacing(1),
  },
  actionsSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  actionsSectionItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  primaryButton: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  destructiveButton: {
    borderColor: theme.palette.error.main,
    color: theme.palette.error.main,
  },
}));

const formatKycTimer = (seconds: number | null) => {
  if (typeof seconds !== 'number' || !Number.isFinite(seconds)) {
    return '--:--';
  }
  if (seconds === -1 || seconds === 0) {
    return '--:--';
  }

  // Assumes API returns seconds; display HH:MM by truncating seconds.
  const safeSeconds = Math.max(0, Math.floor(seconds));
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

interface Props {
  user: UserDetails;
}

const IdentityVerification = ({ user }: Props) => {
  const { id: userId, kycStatus } = user;

  const classes = useStyles();
  const dataProvider = useDataProvider();
  const refresh = useRefresh();
  const notify = useNotify();
  const version = useVersion();

  const [kycLoading, setKycLoading] = useState<boolean>(false);
  const [kycTimerSeconds, setKycTimerSeconds] = useState<number | null>(null);

  useEffect(() => {
    if (!userId) {
      setKycTimerSeconds(null);
      return;
    }

    const fetchKycTimer = async () => {
      try {
        // Assumes API returns seconds and ignores the appended userId query param.
        const { data } = await dataProvider.get('userKycTimer', { userId });
        const nextValue = typeof data === 'number' ? data : Number(data);

        setKycTimerSeconds(Number.isFinite(nextValue) ? nextValue : null);
      } catch (error: any) {
        notify(
          typeof error === 'string' ? error : error.message || 'ra.notification.http_error',
          'warning'
        );
        setKycTimerSeconds(null);
      }
    };

    fetchKycTimer();
  }, [dataProvider, notify, userId, version]);

  // Assumes user id matches {userId} for KYC endpoints and the API accepts empty bodies.
  const handleStartKyc = async () => {
    if (!userId) {
      return;
    }

    try {
      setKycLoading(true);

      await dataProvider.post('userKyc', { userId });

      refresh();
    } catch (error: any) {
      notify(
        typeof error === 'string' ? error : error.message || 'ra.notification.http_error',
        'warning'
      );
    } finally {
      setKycLoading(false);
    }
  };

  const handleResetKycTimer = async () => {
    if (!userId) {
      return;
    }

    try {
      setKycLoading(true);

      await dataProvider.update('userKycTimer', { userId });

      refresh();
    } catch (error: any) {
      notify(
        typeof error === 'string' ? error : error.message || 'ra.notification.http_error',
        'warning'
      );
    } finally {
      setKycLoading(false);
    }
  };

  const handleDropKycStatus = async () => {
    if (!userId) {
      return;
    }

    try {
      setKycLoading(true);

      await dataProvider.delete('userKyc', { userId });

      refresh();
    } catch (error: any) {
      notify(
        typeof error === 'string' ? error : error.message || 'ra.notification.http_error',
        'warning'
      );
    } finally {
      setKycLoading(false);
    }
  };

  const handleCompleteKycStatus = async () => {
    if (!userId) {
      return;
    }

    try {
      setKycLoading(true);

      await dataProvider.update('userKycComplete', { userId });

      refresh();
    } catch (error: any) {
      notify(
        typeof error === 'string' ? error : error.message || 'ra.notification.http_error',
        'warning'
      );
    } finally {
      setKycLoading(false);
    }
  };

  const normalizedStatus = kycStatus ?? 'NOT_STARTED';
  const statusInfoMap: Record<
    string,
    { label: string; description: string; tone: 'success' | 'warning' | 'error' | 'neutral' }
  > = {
    NOT_STARTED: {
      label: 'Not started',
      description: 'No verification request has been initiated yet.',
      tone: 'neutral',
    },
    REQUIRED: {
      label: 'Required',
      description: 'Verification is required before access can continue.',
      tone: 'warning',
    },
    STARTED: {
      label: 'In progress',
      description: 'User has started the verification process.',
      tone: 'warning',
    },
    COMPLETED: {
      label: 'Completed',
      description: 'Verification has been completed.',
      tone: 'success',
    },
    FAILED: {
      label: 'Failed',
      description: 'Verification failed and needs review.',
      tone: 'error',
    },
  };
  const statusInfo = statusInfoMap[normalizedStatus] ?? statusInfoMap.NOT_STARTED;
  const statusChipClass = classNames(
    classes.statusChip,
    statusInfo.tone === 'success' && classes.statusChipSuccess,
    statusInfo.tone === 'warning' && classes.statusChipWarning,
    statusInfo.tone === 'error' && classes.statusChipError,
    statusInfo.tone === 'neutral' && classes.statusChipNeutral
  );

  const formattedKycTimer = formatKycTimer(kycTimerSeconds);
  const hasValidTimer = typeof kycTimerSeconds === 'number' && Number.isFinite(kycTimerSeconds);
  const isSelfVerification = normalizedStatus !== 'NOT_STARTED' && kycTimerSeconds === -1;
  const kycTypeLabel =
    normalizedStatus === 'NOT_STARTED'
      ? '--'
      : isSelfVerification
        ? 'Self verification'
        : 'Admin request';
  const kycTypeInfo = {
    label: kycTypeLabel,
    helper:
      kycTypeLabel === '--'
        ? 'Verification type is set after the process starts.'
        : isSelfVerification
          ? 'User completes verification without an admin timer.'
          : 'Admin initiated verification with a timer.',
  };
  const timerInfo = (() => {
    if (normalizedStatus === 'NOT_STARTED') {
      return {
        label: 'No active timer',
        helper: 'Timer starts after verification begins.',
      };
    }
    if (isSelfVerification) {
      return {
        label: 'No timer (self verification)',
        helper: 'Self verification does not include a countdown.',
      };
    }
    if (kycTimerSeconds === 0) {
      return {
        label: 'Expired',
        helper: 'Admin-requested verification window has ended.',
      };
    }
    if (!hasValidTimer) {
      return {
        label: 'Unavailable',
        helper: 'Timer could not be loaded.',
      };
    }
    return {
      label: formattedKycTimer,
      helper: 'Time remaining for the user to complete verification.',
    };
  })();

  const isResetTimerDisabled =
    kycLoading ||
    isSelfVerification ||
    (normalizedStatus !== 'FAILED' && normalizedStatus !== 'REQUIRED');
  const isStartDisabled = kycLoading || normalizedStatus !== 'NOT_STARTED';
  const isDropDisabled =
    kycLoading || normalizedStatus === 'NOT_STARTED' || normalizedStatus === 'COMPLETED';
  const isCompleteDisabled =
    kycLoading || normalizedStatus === 'NOT_STARTED' || normalizedStatus === 'COMPLETED';
  const primaryActionKey = !isStartDisabled
    ? 'start'
    : !isCompleteDisabled
      ? 'complete'
      : !isDropDisabled
        ? 'drop'
        : null;
  const primaryActionHelper = primaryActionKey
    ? `Recommended: ${
        primaryActionKey === 'start'
          ? 'Start verification'
          : primaryActionKey === 'complete'
            ? 'Mark as completed'
            : 'Clear verification status'
      }.`
    : 'No primary action is available for the current status.';

  const renderActionButton = (actionKey: 'start' | 'complete' | 'drop' | 'reset') => {
    const isPrimary = primaryActionKey === actionKey;
    const actionConfig =
      actionKey === 'start'
        ? {
            label: 'Start verification',
            onClick: handleStartKyc,
            disabled: isStartDisabled,
          }
        : actionKey === 'complete'
          ? {
              label: 'Mark as completed',
              onClick: handleCompleteKycStatus,
              disabled: isCompleteDisabled,
            }
          : actionKey === 'drop'
            ? {
                label: 'Clear verification status',
                onClick: handleDropKycStatus,
                disabled: isDropDisabled,
                confirmTitle: 'Clear verification status',
                confirmContent: 'Are you sure you want to clear the verification status?',
              }
            : {
                label: 'Reset verification timer',
                onClick: handleResetKycTimer,
                disabled: isResetTimerDisabled,
                confirmTitle: 'Reset verification timer',
                confirmContent: 'Reset the timer for this verification request?',
              };

    const button = (
      <Button
        variant={isPrimary ? 'contained' : 'outlined'}
        className={classNames(
          classes.button,
          isPrimary && classes.primaryButton,
          actionKey === 'drop' && classes.destructiveButton
        )}
        onClick={actionConfig.onClick}
        disabled={actionConfig.disabled}>
        {actionConfig.label}
      </Button>
    );

    if (actionConfig.confirmTitle) {
      return (
        <Confirmation title={actionConfig.confirmTitle} content={actionConfig.confirmContent}>
          {button}
        </Confirmation>
      );
    }

    return button;
  };

  return (
    <Card className={classes.card}>
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <Box className={classes.header}>
            <Typography className={classes.sectionTitle}>Identity verification</Typography>
            <Box className={statusChipClass}>{statusInfo.label}</Box>
          </Box>
          <Typography className={classes.statusDescription}>{statusInfo.description}</Typography>
          {kycLoading && (
            <Box className={classes.loadingRow}>
              <CircularProgress size={16} />
              <Typography className={classes.helperText}>Updating...</Typography>
            </Box>
          )}
        </Grid>

        <Grid item xs={12}>
          <Divider />
        </Grid>

        <Grid item xs={12}>
          <Box className={classes.actionsSection}>
            <Box className={classes.actionsSectionItem}>
              <Typography className={classes.label}>Primary action</Typography>
              <Typography className={classes.helperText}>{primaryActionHelper}</Typography>
              {primaryActionKey && (
                <Box className={classes.actionGroup}>{renderActionButton(primaryActionKey)}</Box>
              )}
            </Box>
            <Box className={classes.actionsSectionItem}>
              <Typography className={classes.label}>Other actions</Typography>
              <Typography className={classes.helperText}>
                Actions are enabled when applicable to the current status or timer.
              </Typography>
              <Box className={classes.actionGroup}>
                {primaryActionKey !== 'start' && renderActionButton('start')}
                {primaryActionKey !== 'complete' && renderActionButton('complete')}
                {primaryActionKey !== 'drop' && renderActionButton('drop')}
                {renderActionButton('reset')}
              </Box>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Divider />
        </Grid>

        <Grid item container xs={12} alignItems="flex-start">
          <Grid item xs={3}>
            <Typography className={classes.label}>KYC type</Typography>
          </Grid>

          <Grid item xs={9}>
            <Typography>{kycTypeInfo.label}</Typography>
            <Typography className={classes.helperText}>{kycTypeInfo.helper}</Typography>
          </Grid>
        </Grid>

        <Grid item container xs={12} alignItems="flex-start">
          <Grid item xs={3}>
            <Typography className={classes.label}>Time left</Typography>
          </Grid>

          <Grid item xs={9}>
            <Typography>{timerInfo.label}</Typography>
            <Typography className={classes.helperText}>{timerInfo.helper}</Typography>
          </Grid>
        </Grid>
      </Grid>
    </Card>
  );
};

export default IdentityVerification;
